import React, { useEffect, useState } from "react";
import Navigation from "../navigation/Navigation";
import styles from "./header.module.css";

// Always show the header within this distance of the top of the page.
const SHOW_NEAR_TOP_PX = 200;
// Ignore scroll movements smaller than this, so trackpad jitter doesn't toggle it.
const SCROLL_THRESHOLD_PX = 10;

/**
 * Hides the header while scrolling down and brings it back on any scroll up.
 */
const useHideOnScroll = (enabled: boolean) => {
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setHidden(false);
      return;
    }

    let lastY = window.scrollY;
    let ticking = false;

    const update = () => {
      const y = window.scrollY;
      const delta = y - lastY;

      if (y < SHOW_NEAR_TOP_PX) {
        setHidden(false);
        lastY = y;
      } else if (Math.abs(delta) > SCROLL_THRESHOLD_PX) {
        setHidden(delta > 0);
        lastY = y;
      }
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [enabled]);

  return [hidden, setHidden] as const;
};

interface HeaderProps {
  // Hide on scroll down / reveal on scroll up, to give pages like /photos more room.
  autoHide?: boolean;
}

const Header: React.FC<HeaderProps> = ({ autoHide = false }) => {
  const [hidden, setHidden] = useHideOnScroll(autoHide);

  return (
    <header
      className={`${autoHide ? styles.autoHide : ""} ${hidden ? styles.hidden : ""}`}
      // Keyboard users tabbing into the nav should always be able to see it.
      onFocus={() => setHidden(false)}
    >
      <h1 className={styles.logo}>Alexandria & Morgan</h1>
      <Navigation />
    </header>
  );
};

export default Header;
